import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import Modal from '../Modal';
import './ReviewModal.css';

// Hook to detect mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (review: { rating: number; comment: string }) => Promise<void>;
  appointmentDetails: {
    serviceName: string;
    masterName: string;
    businessName: string;
    appointmentId: string;
    is_master_business?: boolean;
  };
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  appointmentDetails
}) => {
  const { language } = useLanguage();
  const { colors } = useTheme();
  const { addNotification } = useNotification();
  const isMobile = useIsMobile();
  
  console.log('ReviewModal - appointmentDetails:', appointmentDetails);
  console.log('ReviewModal - is_master_business:', appointmentDetails.is_master_business);
  console.log('ReviewModal - isMobile:', isMobile);
  console.log('ReviewModal - masterName:', appointmentDetails.masterName);
  console.log('ReviewModal - businessName:', appointmentDetails.businessName);
  console.log('ReviewModal - serviceName:', appointmentDetails.serviceName);

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      addNotification(
        language === 'ua' ? 'Будь ласка, виберіть рейтинг' : 'Please select a rating',
        'error'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({ rating, comment });
      addNotification(
        language === 'ua' ? '✅ Відгук успішно додано' : '✅ Review added successfully',
        'success'
      );
      handleClose();
    } catch (error: any) {
      console.error('Error submitting review:', error);
      addNotification(
        error.message || (language === 'ua' ? '❌ Помилка при додаванні відгуку' : '❌ Error adding review'),
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setComment('');
    setHoveredStar(0);
    onClose();
  };

  const renderStars = () => {
    return (
      <div className="stars-container">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star-button ${star <= (hoveredStar || rating) ? 'active' : ''}`}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
            disabled={isSubmitting}
          >
            <i className="bi bi-star-fill"></i>
          </button>
        ))}
      </div>
    );
  };

  const getRatingText = () => {
    if (language === 'ua') {
      switch (rating) {
        case 1: return 'Дуже погано';
        case 2: return 'Погано';
        case 3: return 'Нормально';
        case 4: return 'Добре';
        case 5: return 'Чудово';
        default: return '';
      }
    } else {
      switch (rating) {
        case 1: return 'Very Poor';
        case 2: return 'Poor';
        case 3: return 'Average';
        case 4: return 'Good';
        case 5: return 'Excellent';
        default: return '';
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnBackdrop={true}>
      <div className="review-modal">
        <div className="review-modal-header">
          <h2 style={{ color: colors.textColor }}>
            {language === 'ua' ? 'Залишити відгук' : 'Leave a Review'}
          </h2>
        </div>

        <div className="appointment-info">
          <div className="appointment-details">
            <h4 style={{ color: colors.textColor }}>
              {appointmentDetails.serviceName}
            </h4>
            <p style={{ color: colors.textSecondary }}>
              {isMobile ? (
                <>
                  <i className="bi bi-person" style={{ marginRight: '6px' }}></i>
                  {language === 'ua' ? 'Майстер:' : 'Master:'} {appointmentDetails.masterName}
                </>
              ) : (
                <>
                  {appointmentDetails.is_master_business ? (
                    <>
                      <i className="bi bi-person" style={{ marginRight: '6px' }}></i>
                      {language === 'ua' ? 'Майстер:' : 'Master:'} {appointmentDetails.masterName}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shop" style={{ marginRight: '6px' }}></i>
                      {language === 'ua' ? 'Бізнес:' : 'Business:'} {appointmentDetails.businessName}
                    </>
                  )}
                </>
              )}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="review-form">
          <div className="rating-section">
            <label style={{ color: colors.textColor }}>
              {language === 'ua' ? 'Ваша оцінка:' : 'Your Rating:'}
            </label>
            {renderStars()}
            {rating > 0 && (
              <div className="rating-text" style={{ color: colors.primary }}>
                {getRatingText()}
              </div>
            )}
          </div>

          <div className="comment-section">
            <label htmlFor="comment" style={{ color: colors.textColor }}>
              {language === 'ua' ? 'Коментар (необов\'язково):' : 'Comment (optional):'}
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 1000))}
              placeholder={
                language === 'ua' 
                  ? 'Поділіться своїми враженнями від візиту...'
                  : 'Share your experience from the visit...'
              }
              maxLength={1000}
              rows={4}
              disabled={isSubmitting}
              style={{
                borderColor: colors.borderColor,
                color: colors.textColor,
                backgroundColor: colors.bg
              }}
            />
            <div className="character-count" style={{ color: colors.textSecondary }}>
              {comment.length}/1000
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={isSubmitting}
              style={{ 
                borderColor: '#dc3545',
                color: '#dc3545',
                backgroundColor: 'transparent'
              }}
            >
              {language === 'ua' ? 'Скасувати' : 'Cancel'}
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting || rating === 0}
              style={{ 
                backgroundColor: rating === 0 ? colors.gray : colors.primary,
                color: '#fff'
              }}
            >
              {isSubmitting ? (
                <>
                  <i className="bi bi-arrow-repeat spin"></i>
                  {language === 'ua' ? ' Надсилання...' : ' Sending...'}
                </>
              ) : (
                <>
                  <i className="bi bi-send"></i>
                  {language === 'ua' ? ' Надіслати відгук' : ' Send Review'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ReviewModal;
